import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import {
  FiBarChart2,
  FiTrendingUp,
  FiActivity,
  FiDownload,
  FiMaximize2,
  FiX,
  FiCopy,
  FiCheck,
  FiFileText,
  FiLoader,
  FiCheckCircle,
} from 'react-icons/fi'
import { toast } from 'react-toastify'
import { XLSX, buildCorporateSheet } from '../../utils/excelCorporateStyle'

export interface GisaChartSeries {
  key: string
  name: string
  color: string
}

export interface GisaChartPayload {
  type: 'bar' | 'line' | 'area'
  title: string
  subtitle?: string
  data: Array<{ label: string; [key: string]: any }>
  series: GisaChartSeries[]
}

interface InteractiveChartCardProps {
  id: string
  chartData: GisaChartPayload
  onExportExcel?: () => void
}

export function InteractiveChartCard({
  id,
  chartData,
  onExportExcel,
}: InteractiveChartCardProps) {
  const [chartType, setChartType] = useState<'bar' | 'line' | 'area'>(chartData.type || 'bar')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
  const [downloadState, setDownloadState] = useState<{
    active: boolean
    pct: number
    title: string
    step: string
  }>({
    active: false,
    pct: 0,
    title: '',
    step: '',
  })

  const waitStep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const updateDownloadProgress = (
    active: boolean,
    pct: number,
    title: string,
    step: string,
  ) => {
    setDownloadState({ active, pct, title, step })
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('gid:gisa-download-progress', {
          detail: {
            active,
            progress: pct,
            statusMsg: title,
            stepDetail: step,
          },
        }),
      )
    }
  }

  const primarySeries = chartData.series?.[0] || {
    key: 'Faturamento',
    name: 'Valor',
    color: '#0284c7',
  }

  const dataRows = chartData.data || []
  const values = dataRows.map((row) => Number(row[primarySeries.key]) || 0)
  const maxVal = Math.max(...values, 1)
  const totalVal = values.reduce((acc, v) => acc + v, 0)
  const avgVal = values.length > 0 ? totalVal / values.length : 0
  const peakIdx = values.reduce(
    (bestIdx, v, idx, arr) => (v > (arr[bestIdx] ?? -1) ? idx : bestIdx),
    0,
  )
  const peakLabel = dataRows[peakIdx]?.label || '-'

  const cleanSubtitle = (chartData.subtitle || '').replace(/\)\)+$/, ')')

  const isCurrency =
    primarySeries.key.toLowerCase().includes('faturamento') ||
    primarySeries.key.toLowerCase().includes('valor') ||
    chartData.title.toLowerCase().includes('r$')

  const formatNumber = (val: number, compact = false) => {
    if (isCurrency) {
      if (compact) {
        if (Math.abs(val) >= 1_000_000) return `R$ ${(val / 1_000_000).toFixed(1)}M`
        if (Math.abs(val) >= 1_000) return `R$ ${(val / 1_000).toFixed(0)}k`
      }
      return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
    if (compact) {
      if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`
      if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(0)}k`
    }
    return val.toLocaleString('pt-BR')
  }

  const formatShortLabel = (rawLabel: string, compactMode: boolean) => {
    const clean = String(rawLabel || '').trim()
    if (compactMode) {
      if (/^[A-Za-zÀ-ÿ]{3}\/\d{2,4}$/.test(clean)) {
        return clean.slice(0, 3)
      }
      return clean.length > 8 ? `${clean.slice(0, 7)}…` : clean
    }
    return clean.length > 14 ? clean.slice(0, 14) : clean
  }

  const formatBadgeValue = (val: number, ultraCompact = false) => {
    if (ultraCompact) {
      if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`
      if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(0)}k`
      return String(Math.round(val))
    }
    return formatNumber(val, true)
  }

  const EXTRA_COLUMN_METADATA: Record<
    string,
    { label: string; align: 'left' | 'center' | 'right'; isNumeric?: boolean }
  > = {
    Cod_Dist: { label: 'Cód. Dist', align: 'center' },
    UF_Principal: { label: 'UF Principal', align: 'center' },
    Regional: { label: 'Regional', align: 'center' },
    Telefone: { label: 'Telefone Cadastrado', align: 'center' },
    Responsavel: { label: 'Responsável', align: 'left' },
    PDVs_UF_Principal: { label: 'PDVs na UF Principal', align: 'right', isNumeric: true },
    PDVs_Ativos_Mes: { label: 'PDVs Positivados no Mês', align: 'right', isNumeric: true },
    SKUs_Vendidos: { label: 'SKUs Vendidos', align: 'right', isNumeric: true },
    DiasPendentes: { label: 'Dias Pendentes (7d)', align: 'right', isNumeric: true },
    Volume: { label: 'Volume Movimentado (Qtd)', align: 'right', isNumeric: true },
    Regularidade: { label: 'Situação de Regularidade', align: 'center' },
  }

  const seriesKeys = new Set((chartData.series || []).map((s) => s.key))
  const firstRowKeys = dataRows.length > 0 ? Object.keys(dataRows[0]) : []
  const hasCodDistCol = firstRowKeys.includes('Cod_Dist')
  const extraColumns = firstRowKeys
    .filter((k) => k !== 'label' && k !== 'Cod_Dist' && !seriesKeys.has(k))
    .map((key) => {
      const meta = EXTRA_COLUMN_METADATA[key]
      if (meta) return { key, ...meta }
      const sampleVal = dataRows[0]?.[key]
      const isNum = typeof sampleVal === 'number'
      return {
        key,
        label: key.replace(/_/g, ' '),
        align: (isNum ? 'right' : 'left') as 'left' | 'center' | 'right',
        isNumeric: isNum,
      }
    })

  // Exportar Excel com o Design Corporativo do GID + Barra de Progresso Visual (incluindo Cód. Dist, UF, Telefone, Responsável e séries)
  const handleDownloadExcel = async () => {
    if (onExportExcel) {
      onExportExcel()
      return
    }
    if (downloadState.active) return
    try {
      updateDownloadProgress(
        true,
        24,
        'Gerando Planilha Corporativa (.xlsx)...',
        'Etapa 1/3: Estruturando colunas cadastrais, séries e totalizadores...',
      )
      await waitStep(180)

      const primaryLabelHeader =
        chartData.title.toLowerCase().includes('distribuidor') ||
        chartData.title.toLowerCase().includes('levantamento')
          ? 'Distribuidor Cadastrado'
          : 'Indicador / Período'

      const descriptiveExtras = extraColumns.filter((c) => !c.isNumeric)
      const numericExtras = extraColumns.filter((c) => c.isNumeric)

      const headers = [
        ...(hasCodDistCol
          ? [{ label: 'Cód. Dist', key: 'Cod_Dist', align: 'center' as const }]
          : []),
        { label: primaryLabelHeader, key: 'label', align: 'left' as const },
        ...descriptiveExtras.map((c) => ({
          label: c.label,
          key: c.key,
          align: c.align,
        })),
        ...(chartData.series || []).map((s) => ({
          label: s.name,
          key: s.key,
          align: 'right' as const,
        })),
        ...numericExtras.map((c) => ({
          label: c.label,
          key: c.key,
          align: c.align,
        })),
      ]

      const totalsRow = [
        ...(hasCodDistCol ? ['TOTAL'] : []),
        `TOTAL (${(chartData.data || []).length} REGISTROS)`,
        ...descriptiveExtras.map(() => '—'),
        ...(chartData.series || []).map((s) =>
          (chartData.data || []).reduce((acc, row) => acc + (Number(row[s.key]) || 0), 0),
        ),
        ...numericExtras.map((c) =>
          (chartData.data || []).reduce((acc, row) => acc + (Number(row[c.key]) || 0), 0),
        ),
      ]

      updateDownloadProgress(
        true,
        64,
        'Gerando Planilha Corporativa (.xlsx)...',
        'Etapa 2/3: Aplicando Design Oficial GID (cabeçalho #0F172A e estilo contábil)...',
      )
      await waitStep(220)

      const ws = buildCorporateSheet({
        reportTitle: chartData.title,
        subtitle: `ANÁLISE GID IA — ${chartData.title.toUpperCase()}`,
        periodLabel: cleanSubtitle || 'Análise em Tempo Real',
        distributorLabel: 'Consolidado GID',
        headers,
        dataRows: chartData.data || [],
        totalsRow,
      })

      updateDownloadProgress(
        true,
        90,
        'Gerando Planilha Corporativa (.xlsx)...',
        'Etapa 3/3: Empacotando arquivo Excel para download...',
      )
      await waitStep(160)

      const wb = XLSX.utils.book_new()
      const sheetName = chartData.title.toLowerCase().includes('levantamento')
        ? 'Levantamento_Dists'
        : 'Analise_GID_IA'
      XLSX.utils.book_append_sheet(wb, ws, sheetName)
      const safeTitle = chartData.title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()
      const fileName = `GID_IA_${safeTitle}.xlsx`
      XLSX.writeFile(wb, fileName)

      updateDownloadProgress(
        true,
        100,
        'Planilha Excel exportada com sucesso!',
        `Download concluído: ${fileName}`,
      )
      toast.success('Planilha exportada com o Design Corporativo do GID!')
      await waitStep(680)
    } catch {
      toast.error('Erro ao exportar planilha.')
    } finally {
      updateDownloadProgress(false, 0, '', '')
    }
  }

  // Renderiza e baixa um PNG de Alta Definição (1600x900) exclusivo do Gráfico com Barra de Progresso Visual
  const handleDownloadCleanChartPng = async () => {
    if (downloadState.active) return
    try {
      if (dataRows.length === 0) return
      updateDownloadProgress(
        true,
        22,
        'Gerando Imagem PNG Full HD (1600×900px) do Gráfico...',
        'Etapa 1/3: Preparando canvas de alta definição e KPIs...',
      )
      await waitStep(180)

      const width = 1600
      const height = 900
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const drawRoundRect = (
        x: number,
        y: number,
        w: number,
        h: number,
        r: number,
        fill?: string | CanvasGradient,
        stroke?: string,
        lineWidth = 1,
      ) => {
        ctx.beginPath()
        ctx.moveTo(x + r, y)
        ctx.lineTo(x + w - r, y)
        ctx.quadraticCurveTo(x + w, y, x + w, y + r)
        ctx.lineTo(x + w, y + h - r)
        ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
        ctx.lineTo(x + r, y + h)
        ctx.quadraticCurveTo(x, y + h, x, y + h - r)
        ctx.lineTo(x, y + r)
        ctx.quadraticCurveTo(x, y, x + r, y)
        ctx.closePath()
        if (fill) {
          ctx.fillStyle = fill
          ctx.fill()
        }
        if (stroke) {
          ctx.strokeStyle = stroke
          ctx.lineWidth = lineWidth
          ctx.stroke()
        }
      }

      // 1. Fundo Executivo Dark GID
      const bgGrad = ctx.createLinearGradient(0, 0, width, height)
      bgGrad.addColorStop(0, '#09101f')
      bgGrad.addColorStop(1, '#0f172a')
      ctx.fillStyle = bgGrad
      ctx.fillRect(0, 0, width, height)

      // Barra de topo institucional GID
      const topBarGrad = ctx.createLinearGradient(0, 0, width, 0)
      topBarGrad.addColorStop(0, '#0284c7')
      topBarGrad.addColorStop(0.5, '#38bdf8')
      topBarGrad.addColorStop(1, '#a6c10b')
      ctx.fillStyle = topBarGrad
      ctx.fillRect(0, 0, width, 8)

      // 2. Cabeçalho do Gráfico
      ctx.fillStyle = '#a6c10b'
      ctx.font = 'bold 15px "Segoe UI", Inter, Arial, sans-serif'
      ctx.textAlign = 'left'
      ctx.fillText('GID • INTELIGÊNCIA ANALÍTICA GID IA', 56, 52)

      ctx.fillStyle = '#f8fafc'
      ctx.font = 'bold 30px "Segoe UI", Inter, Arial, sans-serif'
      ctx.fillText(chartData.title, 56, 92)

      if (cleanSubtitle) {
        ctx.fillStyle = '#94a3b8'
        ctx.font = '500 17px "Segoe UI", Inter, Arial, sans-serif'
        ctx.fillText(cleanSubtitle, 56, 122)
      }

      // 3. Cards de Resumo (KPIs do Gráfico)
      const kpiCards = [
        { label: 'TOTAL ACUMULADO', value: formatNumber(totalVal), color: '#38bdf8' },
        { label: 'MÉDIA NO PERÍODO', value: formatNumber(Math.round(avgVal)), color: '#a6c10b' },
        {
          label: `MAIOR PICO (${peakLabel})`,
          value: formatNumber(values[peakIdx] || 0),
          color: '#22c55e',
        },
        {
          label: 'PERÍODOS / ITENS',
          value: `${dataRows.length} registros`,
          color: '#f59e0b',
        },
      ]

      const cardY = 148
      const cardH = 82
      const gap = 20
      const totalCardsW = width - 112
      const cardW = (totalCardsW - gap * 3) / 4

      kpiCards.forEach((kc, idx) => {
        const cx = 56 + idx * (cardW + gap)
        drawRoundRect(cx, cardY, cardW, cardH, 12, '#131f35', '#1e293b', 1.5)
        ctx.fillStyle = kc.color
        ctx.fillRect(cx, cardY + 14, 4, cardH - 28)

        ctx.fillStyle = '#94a3b8'
        ctx.font = 'bold 12px "Segoe UI", Inter, Arial, sans-serif'
        ctx.textAlign = 'left'
        ctx.fillText(kc.label, cx + 18, cardY + 30)

        ctx.fillStyle = '#f8fafc'
        ctx.font = 'bold 21px "Segoe UI", Inter, Arial, sans-serif'
        ctx.fillText(kc.value, cx + 18, cardY + 62)
      })

      // 4. Área Principal do Gráfico
      const boxX = 56
      const boxY = 252
      const boxW = width - 112
      const boxH = 568
      drawRoundRect(boxX, boxY, boxW, boxH, 16, '#0b1324', '#1e293b', 1.5)

      if (chartType === 'bar') {
        const padLeft = boxX + 220
        const padRight = boxX + boxW - 180
        const barAreaW = padRight - padLeft
        const startY = boxY + 42
        const availH = boxH - 76
        const rowH = Math.min(46, availH / Math.max(dataRows.length, 1))
        const barH = Math.max(14, rowH * 0.56)

        dataRows.forEach((row, idx) => {
          const val = Number(row[primarySeries.key]) || 0
          const pct = Math.max(0.02, Math.min(1, val / maxVal))
          const yCenter = startY + idx * rowH + rowH / 2

          // Label à esquerda
          ctx.fillStyle = '#e2e8f0'
          ctx.font = '600 15px "Segoe UI", Inter, Arial, sans-serif'
          ctx.textAlign = 'right'
          ctx.fillText(String(row.label || '').slice(0, 22), padLeft - 18, yCenter + 5)

          // Trilho de fundo
          drawRoundRect(padLeft, yCenter - barH / 2, barAreaW, barH, barH / 2, '#162238')

          // Barra preenchida
          const fillW = Math.max(12, barAreaW * pct)
          const bGrad = ctx.createLinearGradient(padLeft, 0, padLeft + fillW, 0)
          bGrad.addColorStop(0, primarySeries.color || '#0284c7')
          bGrad.addColorStop(1, '#38bdf8')
          drawRoundRect(padLeft, yCenter - barH / 2, fillW, barH, barH / 2, bGrad)

          // Valor à direita
          ctx.fillStyle = '#38bdf8'
          ctx.font = 'bold 15px "Segoe UI", Inter, Arial, sans-serif'
          ctx.textAlign = 'left'
          ctx.fillText(formatNumber(val), padLeft + fillW + 14, yCenter + 5)
        })
      } else {
        // Gráfico de Linha ou Área espaçado em Widescreen
        const plotLeft = boxX + 125
        const plotRight = boxX + boxW - 65
        const plotTop = boxY + 68
        const plotBottom = boxY + boxH - 78
        const plotW = plotRight - plotLeft
        const plotH = plotBottom - plotTop

        // Linhas de grade horizontais (5 níveis) + Escala Y
        const gridSteps = [0, 0.25, 0.5, 0.75, 1]
        gridSteps.forEach((step) => {
          const gy = plotTop + step * plotH
          const scaleVal = maxVal * (1 - step)

          ctx.save()
          ctx.strokeStyle = step === 1 ? '#334155' : '#1e293b'
          ctx.lineWidth = step === 1 ? 1.5 : 1
          if (step < 1) ctx.setLineDash([6, 6])
          ctx.beginPath()
          ctx.moveTo(plotLeft, gy)
          ctx.lineTo(plotRight, gy)
          ctx.stroke()
          ctx.restore()

          ctx.fillStyle = '#64748b'
          ctx.font = '600 13px "Segoe UI", Inter, Arial, sans-serif'
          ctx.textAlign = 'right'
          ctx.fillText(formatNumber(scaleVal, true), plotLeft - 16, gy + 4)
        })

        // Coordenadas dos pontos com amplo espaçamento
        const pts = dataRows.map((row, i) => {
          const val = Number(row[primarySeries.key]) || 0
          const x =
            dataRows.length === 1
              ? plotLeft + plotW / 2
              : plotLeft + (i / (dataRows.length - 1)) * plotW
          const y = plotTop + plotH - (val / maxVal) * plotH
          return { x, y, val, label: String(row.label || '') }
        })

        // Guias verticais sutis
        pts.forEach((p) => {
          ctx.save()
          ctx.strokeStyle = 'rgba(51, 65, 85, 0.35)'
          ctx.lineWidth = 1
          ctx.setLineDash([4, 4])
          ctx.beginPath()
          ctx.moveTo(p.x, p.y)
          ctx.lineTo(p.x, plotBottom)
          ctx.stroke()
          ctx.restore()
        })

        // Preenchimento de Área
        if (chartType === 'area' && pts.length > 1) {
          const areaGrad = ctx.createLinearGradient(0, plotTop, 0, plotBottom)
          areaGrad.addColorStop(0, 'rgba(2, 132, 199, 0.48)')
          areaGrad.addColorStop(1, 'rgba(2, 132, 199, 0.02)')
          ctx.beginPath()
          ctx.moveTo(pts[0].x, plotBottom)
          pts.forEach((p) => ctx.lineTo(p.x, p.y))
          ctx.lineTo(pts[pts.length - 1].x, plotBottom)
          ctx.closePath()
          ctx.fillStyle = areaGrad
          ctx.fill()
        }

        // Linha principal
        if (pts.length > 1) {
          ctx.save()
          ctx.strokeStyle = '#0ea5e9'
          ctx.lineWidth = 4
          ctx.lineCap = 'round'
          ctx.lineJoin = 'round'
          ctx.shadowColor = 'rgba(14, 165, 233, 0.45)'
          ctx.shadowBlur = 12
          ctx.beginPath()
          pts.forEach((p, idx) => {
            if (idx === 0) ctx.moveTo(p.x, p.y)
            else ctx.lineTo(p.x, p.y)
          })
          ctx.stroke()
          ctx.restore()
        }

        // Pontos, Rótulos do Eixo X e Badges de Valor com Anti-Colisão
        pts.forEach((p, idx) => {
          // Ponto circular
          ctx.beginPath()
          ctx.arc(p.x, p.y, 7, 0, Math.PI * 2)
          ctx.fillStyle = '#09101f'
          ctx.fill()
          ctx.lineWidth = 3.5
          ctx.strokeStyle = '#38bdf8'
          ctx.stroke()

          // Rótulo de Mês/Categoria no eixo X
          ctx.fillStyle = '#cbd5e1'
          ctx.font = 'bold 14px "Segoe UI", Inter, Arial, sans-serif'
          ctx.textAlign = 'center'
          ctx.fillText(p.label.slice(0, 12), p.x, plotBottom + 32)

          // Posição vertical inteligente do badge para nunca colidir com vizinhos
          const prev = idx > 0 ? pts[idx - 1] : null
          const isCloseToPrev = prev && Math.abs(p.x - prev.x) < 120 && Math.abs(p.y - prev.y) < 28
          const staggerOffset = isCloseToPrev && idx % 2 === 1 ? -42 : -22
          const badgeCenterY = Math.max(boxY + 26, p.y + staggerOffset)

          const valText = formatNumber(p.val, true)
          ctx.font = 'bold 13px "Segoe UI", Inter, Arial, sans-serif'
          const textW = ctx.measureText(valText).width
          const pillW = textW + 18
          const pillH = 24

          drawRoundRect(
            p.x - pillW / 2,
            badgeCenterY - pillH / 2,
            pillW,
            pillH,
            6,
            '#13233f',
            '#0284c7',
            1.2,
          )

          ctx.fillStyle = '#f8fafc'
          ctx.textAlign = 'center'
          ctx.fillText(valText, p.x, badgeCenterY + 4.5)
        })
      }

      updateDownloadProgress(
        true,
        66,
        'Gerando Imagem PNG Full HD (1600×900px) do Gráfico...',
        'Etapa 2/3: Renderizando série histórica, escalas e rótulos...',
      )
      await waitStep(210)

      // 5. Rodapé Institucional Limpo
      ctx.fillStyle = '#64748b'
      ctx.font = '500 13px "Segoe UI", Inter, Arial, sans-serif'
      ctx.textAlign = 'between' as any
      ctx.textAlign = 'left'
      ctx.fillText(
        `Fonte: Base Oficial GID (Grupo GSA) • Exportado em ${new Date().toLocaleString('pt-BR')}`,
        56,
        height - 28,
      )
      ctx.textAlign = 'right'
      ctx.fillStyle = '#a6c10b'
      ctx.font = 'bold 13px "Segoe UI", Inter, Arial, sans-serif'
      ctx.fillText('GISA SQL ANALYTICS', width - 56, height - 28)

      updateDownloadProgress(
        true,
        92,
        'Gerando Imagem PNG Full HD (1600×900px) do Gráfico...',
        'Etapa 3/3: Convertendo imagem PNG e iniciando download...',
      )
      await waitStep(150)

      const url = canvas.toDataURL('image/png')
      const link = document.createElement('a')
      link.href = url
      const safeTitle = chartData.title.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()
      const fileName = `grafico_gisa_${safeTitle}.png`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      updateDownloadProgress(
        true,
        100,
        'Imagem PNG Full HD baixada com sucesso!',
        `Download concluído: ${fileName}`,
      )
      toast.success('Imagem PNG do gráfico baixada em alta definição!')
      await waitStep(680)
    } catch {
      toast.error('Não foi possível gerar a imagem PNG do gráfico.')
    } finally {
      updateDownloadProgress(false, 0, '', '')
    }
  }

  const renderDownloadProgressBanner = () => {
    if (!downloadState.active) return null
    const isDone = downloadState.pct >= 100
    return (
      <div
        style={{
          marginBottom: 10,
          padding: '9px 12px',
          borderRadius: 10,
          background: isDone ? 'rgba(16, 185, 129, 0.16)' : 'rgba(37, 99, 235, 0.18)',
          border: `1px solid ${isDone ? 'rgba(16, 185, 129, 0.45)' : 'rgba(56, 189, 248, 0.4)'}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              fontSize: 11.5,
              fontWeight: 700,
              color: isDone ? '#6ee7b7' : '#e0f2fe',
            }}
          >
            {isDone ? (
              <FiCheckCircle size={14} color='#10b981' />
            ) : (
              <FiLoader size={14} color='#38bdf8' />
            )}
            <span>{downloadState.title}</span>
          </div>
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 800,
              padding: '2px 7px',
              borderRadius: 999,
              background: isDone ? '#10b981' : '#0284c7',
              color: '#ffffff',
            }}
          >
            {Math.round(downloadState.pct)}%
          </span>
        </div>

        <div
          style={{
            width: '100%',
            height: 6,
            borderRadius: 999,
            background: 'rgba(15, 23, 42, 0.85)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${Math.max(5, Math.min(100, downloadState.pct))}%`,
              height: '100%',
              borderRadius: 999,
              background: isDone
                ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                : 'linear-gradient(90deg, #0284c7 0%, #38bdf8 60%, #10b981 100%)',
              transition: 'width 0.22s ease',
            }}
          />
        </div>

        {downloadState.step && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10.5,
              color: '#94a3b8',
            }}
          >
            <span>{downloadState.step}</span>
            <span>{isDone ? 'Concluído' : 'Gerando...'}</span>
          </div>
        )}
      </div>
    )
  }

  // Copiar resumo textual
  const handleCopySummary = () => {
    const lines = [`📊 ${chartData.title}`]
    if (cleanSubtitle) lines.push(cleanSubtitle)
    lines.push('')
    chartData.data.forEach((row) => {
      const details = chartData.series
        .map((s) => `${s.name}: ${formatNumber(Number(row[s.key]) || 0)}`)
        .join(' | ')
      lines.push(`• ${row.label}: ${details}`)
    })
    navigator.clipboard.writeText(lines.join('\n'))
    setCopied(true)
    toast.success('Resumo copiado para a área de transferência!')
    setTimeout(() => setCopied(false), 2000)
  }

  const renderSvgChart = (height = 225, isFullscreen = false) => {
    const data = chartData.data || []
    if (data.length === 0) return null

    if (chartType === 'bar') {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isFullscreen ? '10px' : '7px',
            padding: '6px 2px',
          }}
        >
          {data.map((row, idx) => {
            const val = Number(row[primarySeries.key]) || 0
            const pct = Math.max(6, Math.min(100, Math.round((val / maxVal) * 100)))
            return (
              <div
                key={idx}
                onMouseEnter={() => setHoveredIdx(idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: isFullscreen ? '6px 10px' : '4px 6px',
                  borderRadius: '6px',
                  background: hoveredIdx === idx ? 'rgba(56, 189, 248, 0.12)' : 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: isFullscreen ? '13px' : '11.5px',
                  }}
                >
                  <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{row.label}</span>
                  <span style={{ color: '#38bdf8', fontWeight: 700, fontFamily: 'monospace' }}>
                    {formatNumber(val)}
                  </span>
                </div>
                <div
                  style={{
                    width: '100%',
                    height: isFullscreen ? '11px' : '8px',
                    background: 'rgba(15, 23, 42, 0.75)',
                    borderRadius: '999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: `linear-gradient(90deg, ${primarySeries.color}, #38bdf8)`,
                      borderRadius: '999px',
                      transition: 'width 0.35s ease',
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    // No card do chat (inline), usa largura responsiva 100% sem scroll horizontal;
    // No modal de Tela Cheia (isFullscreen), usa largura ampla de 940px+
    const svgWidth = isFullscreen ? Math.max(940, data.length * 84 + 110) : 520
    const hitWidth = isFullscreen ? 80 : Math.max(24, Math.floor(420 / Math.max(data.length, 1)))
    const svgHeight = height
    const padLeft = isFullscreen ? 78 : 54
    const padRight = isFullscreen ? 44 : 22
    const padTop = isFullscreen ? 44 : 34
    const padBottom = isFullscreen ? 40 : 28
    const usableW = svgWidth - padLeft - padRight
    const usableH = svgHeight - padTop - padBottom

    const points = data.map((row, i) => {
      const val = Number(row[primarySeries.key]) || 0
      const x =
        data.length === 1 ? padLeft + usableW / 2 : padLeft + (i / (data.length - 1)) * usableW
      const y = padTop + usableH - (val / maxVal) * usableH
      return { x, y, val, label: String(row.label || ''), rawRow: row }
    })

    const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(' ')
    const areaPath =
      points.length > 1
        ? `M ${points[0].x},${padTop + usableH} L ${points
            .map((p) => `${p.x},${p.y}`)
            .join(' L ')} L ${points[points.length - 1].x},${padTop + usableH} Z`
        : ''

    const gradId = `grad-${id}-${isFullscreen ? 'full' : 'inline'}`
    const activePoint = hoveredIdx !== null && points[hoveredIdx] ? points[hoveredIdx] : null

    return (
      <div style={{ width: '100%', position: 'relative' }}>
        {/* Barra de detalhe interativa ao passar o mouse */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            padding: '6px 10px',
            marginBottom: 8,
            borderRadius: 8,
            background: 'rgba(15, 23, 42, 0.7)',
            border: '1px solid rgba(51, 65, 85, 0.7)',
            fontSize: isFullscreen ? 12.5 : 11,
            color: '#cbd5e1',
          }}
        >
          {activePoint ? (
            <>
              <span>
                📅 Período Selecionado:{' '}
                <strong style={{ color: '#38bdf8' }}>{activePoint.label}</strong>
              </span>
              <span>
                {primarySeries.name}:{' '}
                <strong style={{ color: '#4ade80', fontFamily: 'monospace' }}>
                  {formatNumber(activePoint.val)}
                </strong>
                {activePoint.rawRow?.Volume !== undefined && (
                  <>
                    {' '}
                    • Volume:{' '}
                    <strong style={{ color: '#f8fafc' }}>
                      {Number(activePoint.rawRow.Volume || 0).toLocaleString('pt-BR')}
                    </strong>
                  </>
                )}
              </span>
            </>
          ) : (
            <>
              <span>
                Total:{' '}
                <strong style={{ color: '#38bdf8' }}>
                  {formatNumber(totalVal, !isFullscreen)}
                </strong>
              </span>
              <span>
                Pico ({peakLabel}):{' '}
                <strong style={{ color: '#4ade80' }}>
                  {formatNumber(values[peakIdx] || 0, !isFullscreen)}
                </strong>
              </span>
            </>
          )}
        </div>

        {/* Container 100% responsivo sem scroll horizontal no chat */}
        <div
          style={{
            width: '100%',
            maxWidth: '100%',
            overflowX: isFullscreen ? 'auto' : 'hidden',
          }}
        >
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            style={{
              width: '100%',
              maxWidth: '100%',
              height: `${svgHeight}px`,
              display: 'block',
            }}
          >
            <defs>
              <linearGradient id={gradId} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={primarySeries.color} stopOpacity={0.48} />
                <stop offset='100%' stopColor={primarySeries.color} stopOpacity={0.03} />
              </linearGradient>
            </defs>

            {/* Linhas de grade horizontais + Escala Y à esquerda */}
            {[0, 0.25, 0.5, 0.75, 1].map((step, idx) => {
              const y = padTop + step * usableH
              const scaleVal = maxVal * (1 - step)
              return (
                <g key={idx}>
                  <line
                    x1={padLeft}
                    y1={y}
                    x2={svgWidth - padRight}
                    y2={y}
                    stroke={step === 1 ? '#475569' : '#1e293b'}
                    strokeDasharray={step === 1 ? undefined : '4 4'}
                    strokeWidth={step === 1 ? '1.4' : '1'}
                  />
                  <text
                    x={padLeft - 10}
                    y={y + 3.5}
                    textAnchor='end'
                    fill='#64748b'
                    fontSize={isFullscreen ? '11' : '9.5'}
                    fontWeight='600'
                  >
                    {formatNumber(scaleVal, true)}
                  </text>
                </g>
              )
            })}

            {/* Guias verticais por mês */}
            {points.map((p, idx) => (
              <line
                key={`vguide-${idx}`}
                x1={p.x}
                y1={p.y}
                x2={p.x}
                y2={padTop + usableH}
                stroke={hoveredIdx === idx ? '#38bdf8' : 'rgba(51, 65, 85, 0.4)'}
                strokeDasharray='3 3'
                strokeWidth={hoveredIdx === idx ? '1.5' : '1'}
              />
            ))}

            {chartType === 'area' && areaPath && <path d={areaPath} fill={`url(#${gradId})`} />}

            {points.length > 1 && (
              <polyline
                fill='none'
                stroke='#0ea5e9'
                strokeWidth={isFullscreen ? '3.5' : '3'}
                strokeLinecap='round'
                strokeLinejoin='round'
                points={polylinePoints}
              />
            )}

            {points.map((p, idx) => {
              const isHovered = hoveredIdx === idx
              const prev = idx > 0 ? points[idx - 1] : null
              const isCloseY = prev && Math.abs(p.y - prev.y) < 26
              // Alterna a altura do badge quando dois meses seguidos têm valores parecidos
              const yOffset = isCloseY && idx % 2 === 1 ? -28 : -14
              const badgeY = Math.max(13, p.y + yOffset)
              const valStr = formatBadgeValue(p.val, !isFullscreen && points.length > 6)
              const pillW = Math.max(
                isFullscreen ? 48 : 34,
                valStr.length * (isFullscreen ? 7.2 : 5.8) + (isFullscreen ? 12 : 8),
              )
              const pillH = isFullscreen ? 20 : 15

              // No modo compacto (chat) com muitos pontos, mostra os badges de forma intercalada + Pico + Último + Hovered para não poluir
              const showBadge =
                isFullscreen ||
                points.length <= 7 ||
                isHovered ||
                idx === 0 ||
                idx === points.length - 1 ||
                idx === peakIdx ||
                idx % 2 === 0

              return (
                <g
                  key={idx}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Área invisível para facilitar hover */}
                  <rect
                    x={p.x - hitWidth / 2}
                    y={padTop}
                    width={hitWidth}
                    height={usableH + padBottom}
                    fill='transparent'
                  />

                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={isHovered ? 6 : isFullscreen ? 4.5 : 3.8}
                    fill='#0f172a'
                    stroke={isHovered ? '#38bdf8' : primarySeries.color}
                    strokeWidth='2.5'
                  />

                  {showBadge && (
                    <>
                      <rect
                        x={p.x - pillW / 2}
                        y={badgeY - pillH / 2 - 2}
                        width={pillW}
                        height={pillH}
                        rx={4}
                        fill={isHovered ? '#0284c7' : '#0f172a'}
                        stroke={isHovered ? '#38bdf8' : '#334155'}
                        strokeWidth='1'
                      />
                      <text
                        x={p.x}
                        y={badgeY + 1.5}
                        textAnchor='middle'
                        fill='#f8fafc'
                        fontSize={isFullscreen ? '11' : '8.8'}
                        fontWeight='700'
                      >
                        {valStr}
                      </text>
                    </>
                  )}

                  {/* Rótulo do eixo X */}
                  <text
                    x={p.x}
                    y={svgHeight - 8}
                    textAnchor='middle'
                    fill={isHovered ? '#38bdf8' : '#cbd5e1'}
                    fontSize={isFullscreen ? '12' : '9.5'}
                    fontWeight={isHovered ? '700' : '600'}
                  >
                    {formatShortLabel(p.label, !isFullscreen)}
                  </text>
                </g>
              )
            })}
          </svg>
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        style={{
          marginTop: 12,
          marginBottom: 8,
          background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: 12,
          padding: '12px 14px',
          border: '1px solid #334155',
          boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Cabeçalho e Controles */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 10,
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#f8fafc' }}>
              📊 {chartData.title}
            </div>
            {cleanSubtitle && (
              <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{cleanSubtitle}</div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              background: '#090d16',
              padding: 3,
              borderRadius: 8,
              border: '1px solid #1e293b',
            }}
          >
            <button
              type='button'
              onClick={() => setChartType('bar')}
              title='Gráfico de Barras'
              style={{
                background: chartType === 'bar' ? '#2563eb' : 'transparent',
                color: chartType === 'bar' ? '#fff' : '#94a3b8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiBarChart2 size={13} />
            </button>
            <button
              type='button'
              onClick={() => setChartType('line')}
              title='Gráfico de Linhas'
              style={{
                background: chartType === 'line' ? '#2563eb' : 'transparent',
                color: chartType === 'line' ? '#fff' : '#94a3b8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiTrendingUp size={13} />
            </button>
            <button
              type='button'
              onClick={() => setChartType('area')}
              title='Gráfico de Área'
              style={{
                background: chartType === 'area' ? '#2563eb' : 'transparent',
                color: chartType === 'area' ? '#fff' : '#94a3b8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiActivity size={13} />
            </button>

            <div style={{ width: 1, height: 14, background: '#334155', margin: '0 2px' }} />

            <button
              type='button'
              onClick={handleCopySummary}
              title='Copiar resumo dos números'
              style={{
                background: 'transparent',
                color: copied ? '#22c55e' : '#94a3b8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {copied ? <FiCheck size={13} /> : <FiCopy size={13} />}
            </button>

            <button
              type='button'
              onClick={handleDownloadExcel}
              disabled={downloadState.active}
              title='Exportar estas informações para Excel (.xlsx)'
              style={{
                background: 'rgba(16, 185, 129, 0.16)',
                color: '#34d399',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                borderRadius: 5,
                padding: '3px 8px',
                fontSize: 11,
                fontWeight: 700,
                cursor: downloadState.active ? 'not-allowed' : 'pointer',
                opacity: downloadState.active ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <FiFileText size={12} />
              <span>Excel</span>
            </button>

            <button
              type='button'
              onClick={handleDownloadCleanChartPng}
              disabled={downloadState.active}
              title='Baixar imagem PNG limpa do gráfico (Alta Definição)'
              style={{
                background: 'transparent',
                color: '#94a3b8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: downloadState.active ? 'not-allowed' : 'pointer',
                opacity: downloadState.active ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiDownload size={13} />
            </button>

            <button
              type='button'
              onClick={() => setIsModalOpen(true)}
              title='Expandir em Tela Cheia'
              style={{
                background: 'transparent',
                color: '#38bdf8',
                border: 'none',
                borderRadius: 5,
                padding: '4px 6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FiMaximize2 size={13} />
            </button>
          </div>
        </div>

        {/* Barra de Progresso de Download no Card */}
        {renderDownloadProgressBanner()}

        {/* Gráfico no Card */}
        {renderSvgChart(220, false)}

        {/* Botão de Ação Direta para Exportar os Dados do Chat para Excel */}
        <div
          style={{
            marginTop: 10,
            paddingTop: 9,
            borderTop: '1px solid rgba(51, 65, 85, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type='button'
            onClick={handleDownloadExcel}
            disabled={downloadState.active}
            style={{
              background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              padding: '6px 12px',
              fontSize: 11.5,
              fontWeight: 700,
              cursor: downloadState.active ? 'not-allowed' : 'pointer',
              opacity: downloadState.active ? 0.65 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
            }}
          >
            <FiFileText size={13} />
            <span>Exportar estas informações para Excel (.xlsx)</span>
          </button>

          <button
            type='button'
            onClick={() => setIsModalOpen(true)}
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#cbd5e1',
              border: '1px solid #334155',
              borderRadius: 8,
              padding: '6px 10px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <FiMaximize2 size={12} color='#38bdf8' />
            <span>Ver Tabela Completa</span>
          </button>
        </div>
      </div>

      {/* Modal Tela Cheia renderizado via Portal na raiz do body */}
      {isModalOpen &&
        typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 200050,
              background: 'rgba(2, 6, 23, 0.88)',
              backdropFilter: 'blur(6px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
            }}
            onClick={() => setIsModalOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                maxWidth: '1060px',
                maxHeight: '92vh',
                overflowY: 'auto',
                background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
                borderRadius: '16px',
                border: '1px solid #334155',
                padding: '24px 28px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontSize: '19px', fontWeight: 700, color: '#f8fafc' }}>
                    📊 {chartData.title}
                  </h3>
                  {cleanSubtitle && (
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                      {cleanSubtitle}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* Seletor de tipo de gráfico também na tela cheia */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      background: '#090d16',
                      padding: 4,
                      borderRadius: 8,
                      border: '1px solid #334155',
                    }}
                  >
                    <button
                      type='button'
                      onClick={() => setChartType('bar')}
                      style={{
                        background: chartType === 'bar' ? '#2563eb' : 'transparent',
                        color: chartType === 'bar' ? '#fff' : '#94a3b8',
                        border: 'none',
                        borderRadius: 6,
                        padding: '5px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <FiBarChart2 size={13} /> Barras
                    </button>
                    <button
                      type='button'
                      onClick={() => setChartType('line')}
                      style={{
                        background: chartType === 'line' ? '#2563eb' : 'transparent',
                        color: chartType === 'line' ? '#fff' : '#94a3b8',
                        border: 'none',
                        borderRadius: 6,
                        padding: '5px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <FiTrendingUp size={13} /> Linhas
                    </button>
                    <button
                      type='button'
                      onClick={() => setChartType('area')}
                      style={{
                        background: chartType === 'area' ? '#2563eb' : 'transparent',
                        color: chartType === 'area' ? '#fff' : '#94a3b8',
                        border: 'none',
                        borderRadius: 6,
                        padding: '5px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <FiActivity size={13} /> Área
                    </button>
                  </div>

                  <button
                    type='button'
                    onClick={handleDownloadExcel}
                    disabled={downloadState.active}
                    style={{
                      background: '#1e293b',
                      color: '#e2e8f0',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      padding: '7px 13px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: downloadState.active ? 'not-allowed' : 'pointer',
                      opacity: downloadState.active ? 0.55 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <FiFileText size={14} color='#38bdf8' /> Exportar Excel
                  </button>
                  <button
                    type='button'
                    onClick={handleDownloadCleanChartPng}
                    disabled={downloadState.active}
                    style={{
                      background: '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '7px 13px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: downloadState.active ? 'not-allowed' : 'pointer',
                      opacity: downloadState.active ? 0.55 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <FiDownload size={14} /> Baixar PNG do Gráfico
                  </button>
                  <button
                    type='button'
                    onClick={() => setIsModalOpen(false)}
                    style={{
                      background: 'rgba(239, 68, 68, 0.15)',
                      color: '#f87171',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: 8,
                      padding: '7px 9px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>

              {/* Barra de Progresso de Download no Modal */}
              {renderDownloadProgressBanner()}

              {/* Gráfico Amplo em Tela Cheia */}
              <div
                style={{
                  background: '#090d16',
                  border: '1px solid #1e293b',
                  borderRadius: '12px',
                  padding: '16px 18px',
                }}
              >
                {renderSvgChart(340, true)}
              </div>

              {/* Tabela Analítica */}
              <div
                style={{
                  maxHeight: '240px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  borderRadius: '10px',
                  border: '1px solid #334155',
                  background: '#090d16',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                  <thead>
                    <tr style={{ background: '#1e293b', color: '#94a3b8', textAlign: 'left' }}>
                      {hasCodDistCol && (
                        <th
                          style={{
                            padding: '9px 14px',
                            borderBottom: '1px solid #334155',
                            textAlign: 'center',
                          }}
                        >
                          Cód. Dist
                        </th>
                      )}
                      <th style={{ padding: '9px 14px', borderBottom: '1px solid #334155' }}>
                        {chartData.title.toLowerCase().includes('distribuidor') ||
                        chartData.title.toLowerCase().includes('levantamento')
                          ? 'Distribuidor Cadastrado'
                          : 'Período / Indicador'}
                      </th>
                      {extraColumns
                        .filter((c) => !c.isNumeric)
                        .map((c) => (
                          <th
                            key={c.key}
                            style={{
                              padding: '9px 14px',
                              borderBottom: '1px solid #334155',
                              textAlign: c.align,
                            }}
                          >
                            {c.label}
                          </th>
                        ))}
                      {chartData.series.map((s) => (
                        <th
                          key={s.key}
                          style={{
                            padding: '9px 14px',
                            borderBottom: '1px solid #334155',
                            textAlign: 'right',
                          }}
                        >
                          {s.name}
                        </th>
                      ))}
                      {extraColumns
                        .filter((c) => c.isNumeric)
                        .map((c) => (
                          <th
                            key={c.key}
                            style={{
                              padding: '9px 14px',
                              borderBottom: '1px solid #334155',
                              textAlign: 'right',
                            }}
                          >
                            {c.label}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.data.map((row, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom: '1px solid #1e293b',
                          color: '#e2e8f0',
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(30, 41, 59, 0.35)',
                        }}
                      >
                        {hasCodDistCol && (
                          <td
                            style={{
                              padding: '8px 14px',
                              textAlign: 'center',
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              color: '#38bdf8',
                            }}
                          >
                            {String(row.Cod_Dist ?? '-')}
                          </td>
                        )}
                        <td style={{ padding: '8px 14px', fontWeight: 600 }}>{row.label}</td>
                        {extraColumns
                          .filter((c) => !c.isNumeric)
                          .map((c) => (
                            <td
                              key={c.key}
                              style={{
                                padding: '8px 14px',
                                textAlign: c.align,
                                color: '#cbd5e1',
                              }}
                            >
                              {String(row[c.key] ?? '-')}
                            </td>
                          ))}
                        {chartData.series.map((s) => (
                          <td
                            key={s.key}
                            style={{
                              padding: '8px 14px',
                              textAlign: 'right',
                              fontFamily: 'monospace',
                              fontWeight: 700,
                              color: s.color,
                            }}
                          >
                            {formatNumber(Number(row[s.key]) || 0)}
                          </td>
                        ))}
                        {extraColumns
                          .filter((c) => c.isNumeric)
                          .map((c) => (
                            <td
                              key={c.key}
                              style={{
                                padding: '8px 14px',
                                textAlign: 'right',
                                fontFamily: 'monospace',
                                color: '#f8fafc',
                              }}
                            >
                              {Number(row[c.key] || 0).toLocaleString('pt-BR')}
                            </td>
                          ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
